import { useEffect } from 'react'

/**
 * ProductsCarousel - drives the "Six products" carousel by button click.
 *
 * The section (#products) used to be a tall pinned scroll-carousel where
 * vertical page scroll translated the track horizontally. That mechanic is
 * disabled (see the initPinScroll early-return in Pentabay.html) and the
 * section is un-pinned to a compact height via StyleOverrides. Here we
 * wire the prev/next buttons to scroll the now-normal horizontal track by
 * one card, and keep the existing progress dots / bar / "NN / 06" meta in
 * sync with the track's scroll position.
 *
 * Desktop only - below lg the cards stack/wrap and the progress row is
 * hidden, so there is nothing to control. Follows the existing DOM-overlay
 * pattern (MutationObserver picks up the homepage when HtmlPage mounts it).
 */
function cardStep(track) {
  const cards = track.querySelectorAll('.prod-card')
  if (cards.length >= 2) {
    const step = cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left
    if (step > 0) return step
  }
  if (cards.length === 1) return cards[0].getBoundingClientRect().width + 24
  return track.clientWidth * 0.6
}

export default function ProductsCarousel() {
  useEffect(() => {
    const updaters = new Set()
    const autoStoppers = new Set()
    const reduce =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    function initProducts(section) {
      if (section.dataset.pcReady === '1') return
      const track = section.querySelector('#prodTrack')
      if (!track) return
      const prev = section.querySelector('#prodPrev')
      const next = section.querySelector('#prodNext')
      const dots = section.querySelector('#prodDots')
      const meta = section.querySelector('#prodMeta')
      const bar = section.querySelector('.pin-h-bar > i')
      const total = section.querySelectorAll('.prod-card').length || 6
      section.dataset.pcReady = '1'

      function update() {
        if (!track.isConnected) { updaters.delete(update); return }
        const max = Math.max(0, track.scrollWidth - track.clientWidth)
        const progress = max > 0 ? track.scrollLeft / max : 0
        const idx = Math.round(progress * (total - 1))
        if (bar) bar.style.width = (progress * 100).toFixed(1) + '%'
        // Single active dot, not a cumulative fill. The old `i <= idx` filled
        // every dot up to the current card, which paired with the progress
        // bar that used to sit beside it - with that bar removed, a filled
        // run of six elongated dots just read as a bar again.
        if (dots) dots.querySelectorAll('i').forEach((d, i) => d.classList.toggle('on', i === idx))
        if (meta) meta.textContent = String(idx + 1).padStart(2, '0') + ' / ' + String(total).padStart(2, '0')
        if (prev) prev.disabled = track.scrollLeft <= 2
        if (next) next.disabled = track.scrollLeft >= max - 2
      }

      if (prev) prev.addEventListener('click', () => track.scrollBy({ left: -cardStep(track), behavior: 'smooth' }))
      if (next) next.addEventListener('click', () => track.scrollBy({ left: cardStep(track), behavior: 'smooth' }))

      let raf = 0
      track.addEventListener('scroll', () => {
        if (raf) return
        raf = requestAnimationFrame(() => { raf = 0; update() })
      }, { passive: true })

      updaters.add(update)
      update()

      // ---- auto-run: advance one card every few seconds so the carousel
      // is always visibly "running". Ping-pongs (reverses at each end) via
      // scrollBy (direct scrollLeft is locked by the track's scroll-snap).
      // Deliberately does NOT pause on hover - that made it look stopped;
      // it only pauses for reduced-motion, a hidden tab, or briefly after
      // the user drives it with the prev/next buttons.
      let dir = 1
      let pauseUntil = 0
      let autoTimer = null
      const step = () => {
        if (!track.isConnected) { clearInterval(autoTimer); autoTimer = null; return }
        if (document.hidden || performance.now() < pauseUntil) return
        const max = Math.max(0, track.scrollWidth - track.clientWidth)
        if (max < 8) return // stacked / all cards visible (mobile)
        if (dir > 0 && track.scrollLeft >= max - 4) dir = -1
        else if (dir < 0 && track.scrollLeft <= 4) dir = 1
        track.scrollBy({ left: dir * cardStep(track), behavior: 'smooth' })
      }
      const bump = () => { pauseUntil = performance.now() + 4000 } // let manual nav settle
      if (prev) prev.addEventListener('click', bump)
      if (next) next.addEventListener('click', bump)
      if (!reduce) autoTimer = setInterval(step, 2600)
      autoStoppers.add(() => { if (autoTimer) clearInterval(autoTimer) })
    }

    function initAll() {
      document.querySelectorAll('#products:not([data-pc-ready="1"])').forEach(initProducts)
    }
    initAll()

    const mo = new MutationObserver(initAll)
    mo.observe(document.body, { childList: true, subtree: true })

    const onResize = () => updaters.forEach((fn) => fn())
    window.addEventListener('resize', onResize)

    return () => {
      mo.disconnect()
      window.removeEventListener('resize', onResize)
      autoStoppers.forEach((fn) => fn())
    }
  }, [])
  return null
}
